// Browser-local suggestions. No Swish writes, cart calls, pixels or email events.
export const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
export const ACTIVE_VIEW_MS = 60000;
export const DATA_KEY = 'sc:saved-for-you:v1';
export const PREF_KEY = 'sc:saved-for-you:off';
const NAV_KEY = 'sc:saved-for-you:last-page';
export const emptyState = () => ({ version: 1, visits: {}, items: {}, dismissals: {} });
export const itemKey = item => `${item.productId}:${item.variantId}`;

export function cleanProduct(item) {
  if (!item || !/^\d+$/.test(String(item.productId)) || !/^\d+$/.test(String(item.variantId)) ||
      !/^[a-z0-9-]+$/.test(item.handle || '')) return null;
  const text = value => String(value || '').slice(0, 180);
  let image = '';
  try {
    const url = new URL(item.image, 'https://sofaclub.co.uk');
    if (url.protocol === 'https:' && /(^|\.)(sofaclub\.co\.uk|shopify\.com)$/.test(url.hostname)) image = url.href;
  } catch {}
  return { productId: String(item.productId), variantId: String(item.variantId), handle: item.handle,
    range: text(item.range), detail: text(item.detail), variant: text(item.variant), price: text(item.price), image };
}

export function pruneState(input, now) {
  const state = emptyState();
  if (input?.version !== 1) return state;
  for (const [key, until] of Object.entries(input.dismissals || {})) {
    if (/^\d+:\d+$/.test(key) && Number.isFinite(until) && until > now) state.dismissals[key] = until;
  }
  for (const [key, entry] of Object.entries(input.items || {}).slice(-100)) {
    const product = cleanProduct(entry);
    if (product && key === itemKey(product) && Number.isFinite(entry.lastSeen) && entry.lastSeen > now - WINDOW_MS &&
        entry.lastSeen <= now && !state.dismissals[key]) {
      state.items[key] = { ...product, added: Number(entry.added) || entry.lastSeen, lastSeen: entry.lastSeen };
    }
  }
  return state;
}

export function dismissItem(input, key, now) {
  const state = pruneState(input, now);
  delete state.items[key]; delete state.visits[key];
  if (/^\d+:\d+$/.test(key)) state.dismissals[key] = now + WINDOW_MS;
  return state;
}

export function clearItems(input, now) {
  let state = pruneState(input, now);
  for (const key of new Set([...Object.keys(state.items), ...Object.keys(state.visits)])) state = dismissItem(state, key, now);
  return state;
}

export function excludeManual(input, keys, now) {
  let state = pruneState(input, now);
  for (const key of keys) if (state.items[key] || state.visits[key]) state = dismissItem(state, key, now);
  return state;
}

export function recordExposure(input, item, exposure, now, manualKeys = []) {
  const state = excludeManual(input, manualKeys, now);
  const product = cleanProduct(item);
  if (!product || !exposure.allowed || exposure.optedOut || !Number.isFinite(exposure.visibleMs) ||
      exposure.visibleMs <= ACTIVE_VIEW_MS) return { state, added: false, qualified: false };
  const key = itemKey(product);
  if (manualKeys.includes(key) || state.dismissals[key]) return { state, added: false, qualified: false };
  const added = !state.items[key];
  state.items[key] = { ...product, added: state.items[key]?.added || now, lastSeen: now };
  return { state: pruneState(state, now), added, qualified: true };
}

export function consentStatus(category, privacy) {
  const method = { preferences: 'preferencesProcessingAllowed', analytics: 'analyticsProcessingAllowed', marketing: 'marketingAllowed' }[category];
  if (!method) return false;
  if (typeof privacy?.[method] !== 'function') return null;
  try { const value = privacy[method](); return typeof value === 'boolean' ? value : null; }
  catch { return null; }
}
export const consentAllowed = (category, privacy) => consentStatus(category, privacy) === true;

export function startSavedForYou(config) {
  const now = () => Date.now();
  let state = emptyState(), allowed = false, manualKeys = [], manualReady = false, timer;
  let optedOut = false, writable = true, page = null, completedEntry = false;
  let visibleMs = 0, visibleSince = null, timedKey = null, timerEpoch = 0, pageActive = true;
  const clock = () => performance.now();
  const hosts = new Map();
  const notify = (name, item = {}, extra = {}, host) => document.dispatchEvent(new CustomEvent('sc:wishlist-action', {
    detail: { name, source: 'saved_for_you', productId: item.productId, variantId: item.variantId,
      surface: host?.root.closest('#sc-swish-dialog') ? 'popup' : 'page', ...extra }
  }));
  const read = (key, storage) => { try { return (storage === 'session' ? window.sessionStorage : window.localStorage).getItem(key); } catch { writable = false; return null; } };
  const write = (key, value, storage) => { try { (storage === 'session' ? window.sessionStorage : window.localStorage).setItem(key, value); } catch { writable = false; } };
  const remove = (key, storage) => { try { (storage === 'session' ? window.sessionStorage : window.localStorage).removeItem(key); } catch { writable = false; } };
  const restore = () => { try { state = pruneState(JSON.parse(read(DATA_KEY) || 'null'), now()); } catch { state = emptyState(); } };
  optedOut = read(PREF_KEY) === '1';
  const save = () => { if (allowed && !optedOut) write(DATA_KEY, JSON.stringify(state)); renderAll(); };
  const path = location.pathname;
  try { page = JSON.parse(document.querySelector('[data-sc-saved-product]')?.textContent || 'null'); } catch {}
  const productForPage = () => {
    if (!page) return null;
    const id = document.querySelector('form.pdp_form input[name="id"], form.outlet_p_form input[name="id"]')?.value ||
      new URL(location.href).searchParams.get('variant') || page.selectedVariant;
    const variant = page.variants?.find(item => String(item.variantId) === String(id));
    return variant ? { ...page, ...variant } : null;
  };
  function stopTimer(reset = false) {
    clearTimeout(timer); timerEpoch++;
    if (visibleSince !== null) visibleMs += Math.max(0, clock() - visibleSince);
    visibleSince = null;
    if (reset) { visibleMs = 0; timedKey = null; completedEntry = false; }
  }
  function schedule() {
    stopTimer();
    const product = cleanProduct(productForPage()), key = product ? itemKey(product) : null;
    if (key !== timedKey) { visibleMs = 0; timedKey = key; completedEntry = false; }
    if (!allowed || optedOut || !writable || !manualReady || !pageActive || location.pathname !== path ||
        !key || completedEntry || document.hidden) return;
    if (manualKeys.includes(key) || state.dismissals[key]) { stopTimer(true); return; }
    visibleSince = clock();
    const epoch = timerEpoch;
    timer = setTimeout(() => {
      if (epoch !== timerEpoch) return;
      if (!consentAllowed(config.dataset.scSavedConsent, window.Shopify?.customerPrivacy) || read(PREF_KEY) === '1') {
        privacyChanged(); return;
      }
      if (document.hidden || !pageActive || location.pathname !== path) { stopTimer(true); return; }
      const current = cleanProduct(productForPage());
      if (!current || itemKey(current) !== timedKey) { schedule(); return; }
      stopTimer();
      if (visibleMs <= ACTIVE_VIEW_MS) { schedule(); return; }
      restore();
      const result = recordExposure(state, current, { allowed, optedOut, visibleMs }, now(), manualKeys);
      state = result.state; completedEntry = true; save();
      if (result.added) showUndo(current);
    }, Math.max(1, Math.ceil(ACTIVE_VIEW_MS - visibleMs + 1)));
  }
  function privacyChanged() {
    const next = consentStatus(config.dataset.scSavedConsent, window.Shopify?.customerPrivacy);
    optedOut = read(PREF_KEY) === '1';
    allowed = next === true;
    if (!allowed || optedOut) {
      stopTimer(true); state = emptyState();
      if (next === false || optedOut) { remove(DATA_KEY); remove(NAV_KEY, 'session'); }
    }
    else { restore(); remove(NAV_KEY, 'session'); }
    state = excludeManual(state, manualKeys, now());
    if (allowed && !optedOut) {
      const stored = read(DATA_KEY), current = JSON.stringify(state);
      if (stored !== null && stored !== current) write(DATA_KEY, current);
    }
    renderAll(); schedule();
  }
  function manualChanged(keys) {
    if (!Array.isArray(keys)) return;
    manualReady = true; manualKeys = keys.filter(key => /^\d+:\d+$/.test(key));
    const before = JSON.stringify(state);
    state = excludeManual(state, manualKeys, now());
    if (before !== JSON.stringify(state)) save();
    schedule();
  }
  function select(host, name) {
    const changed = host.active !== name;
    host.active = name;
    host.page.hidden = name !== 'manual'; host.panel.hidden = name !== 'saved';
    for (const [key, tab] of Object.entries(host.tabs)) {
      tab.setAttribute('aria-selected', String(key === name)); tab.tabIndex = key === name ? 0 : -1;
    }
    if (name === 'saved') { state = excludeManual(state, manualKeys, now()); save(); }
    if (changed) {
      document.dispatchEvent(new CustomEvent('sc:wishlist-action', { detail: { name: 'wishlist_tab_view',
        source: name === 'saved' ? 'saved_for_you' : 'manual', surface: host.root.closest('#sc-swish-dialog') ? 'popup' : 'page' } }));
      if (name === 'saved') notify('saved_for_you_view', {}, { count: Object.keys(state.items).length }, host);
    }
  }
  const element = (tag, text, className) => {
    const node = document.createElement(tag); if (text) node.textContent = text; if (className) node.className = className; return node;
  };
  function render(host) {
    const items = Object.values(pruneState(state, now()).items).sort((a, b) => b.lastSeen - a.lastSeen);
    host.tabs.saved.textContent = `Saved for You${items.length ? ` (${items.length})` : ''}`;
    host.status.textContent = !allowed ? 'Automatic saving is paused. Your Wishlist still works.' : optedOut ?
      'Automatic saving is off.' : !writable ? 'Your browser cannot keep saved suggestions.' : 'Sofas you view for over a minute can appear here.';
    host.toggle.textContent = !allowed ? 'Automatic saving unavailable' : optedOut ? 'Turn on automatic saving' : 'Turn off automatic saving';
    host.toggle.disabled = !allowed; host.toggle.setAttribute('aria-pressed', String(!optedOut && allowed));
    host.clear.disabled = !items.length;
    host.grid.replaceChildren();
    if (!items.length) { host.grid.append(element('p', 'No sofas saved for you yet.', 'sc-saved-empty')); return; }
    for (const item of items) {
      const key = itemKey(item), card = element('article', '', 'sc-saved-card');
      card.dataset.productId = item.productId; card.dataset.variantId = item.variantId;
      const link = element('a'); link.href = `/products/${item.handle}?variant=${item.variantId}`;
      if (item.image) { const image = element('img'); image.src = item.image; image.alt = `${item.range} ${item.detail}`; image.loading = 'lazy'; link.append(image); }
      const title = element('div', '', 'sc-saved-title'); title.append(element('span', item.range, 'sc-wishlist-range'), element('span', item.detail, 'sc-wishlist-detail'));
      link.append(title); card.append(link);
      if (item.variant && item.variant !== 'Default Title') card.append(element('p', item.variant, 'sc-saved-variant'));
      card.append(element('p', item.price, 'sc-saved-price'));
      const promotion = element('div', '', 'sc-saved-promote');
      promotion.dataset.scSavedPromote = ''; promotion.dataset.productHandle = item.handle; promotion.dataset.productId = item.productId; promotion.dataset.variantId = item.variantId;
      const start = element('button', 'Add to Wishlist', 'sc-saved-start'); start.type = 'button'; start.dataset.scSavedStart = '';
      const feedback = element('span', '', 'sc-saved-feedback'); feedback.setAttribute('role', 'status');
      promotion.append(start, feedback); card.append(promotion);
      const dismiss = element('button', 'Remove', 'sc-saved-remove'); dismiss.type = 'button'; dismiss.setAttribute('aria-label', `Remove ${item.range} ${item.detail} from Saved for You`);
      dismiss.addEventListener('click', () => { state = dismissItem(state, key, now()); save(); notify('saved_for_you_dismiss', item, { action: 'remove' }, host); }); card.append(dismiss);
      host.grid.append(card);
    }
  }
  function renderAll() { for (const host of hosts.values()) render(host); }
  function attach(pageElement) {
    if (hosts.has(pageElement)) return;
    const root = element('section', '', 'sc-saved-tabs');
    const index = hosts.size + 1, bar = element('div', '', 'sc-saved-tablist'); bar.setAttribute('role', 'tablist'); bar.setAttribute('aria-label', 'Saved sofas');
    const tabs = {}, panel = element('div', '', 'sc-saved-panel'); panel.id = `sc-saved-panel-${index}`; panel.setAttribute('role', 'tabpanel');
    pageElement.before(root); root.append(bar, pageElement, panel);
    pageElement.id ||= `sc-manual-panel-${index}`; pageElement.setAttribute('role', 'tabpanel');
    const host = { root, page: pageElement, panel, tabs, active: 'manual' }; hosts.set(pageElement, host);
    for (const [name, label, target] of [['saved', 'Saved for You', panel], ['manual', 'Wishlist', pageElement]]) {
      const tab = element('button', label); tab.type = 'button'; tab.id = `sc-${name}-tab-${index}`; tab.setAttribute('role', 'tab'); tab.setAttribute('aria-controls', target.id); target.setAttribute('aria-labelledby', tab.id);
      tab.addEventListener('click', () => select(host, name)); tab.addEventListener('keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault(); const next = event.key === 'Home' ? 'saved' : event.key === 'End' ? 'manual' : name === 'saved' ? 'manual' : 'saved'; select(host, next); tabs[next].focus();
        }
      }); tabs[name] = tab; bar.append(tab);
    }
    panel.append(element('p', 'Saved in this browser only, separately from your Wishlist. Prices from your last visit.', 'sc-saved-intro'));
    host.status = element('p', '', 'sc-saved-status'); host.status.setAttribute('role', 'status'); panel.append(host.status);
    const controls = element('div', '', 'sc-saved-controls'); host.clear = element('button', 'Clear saved suggestions'); host.clear.type = 'button';
    host.clear.addEventListener('click', () => { const count = Object.keys(state.items).length; state = clearItems(state, now()); if (timedKey) state = dismissItem(state, timedKey, now()); stopTimer(true); save(); notify('saved_for_you_clear', {}, { count, action: 'clear' }, host); });
    host.toggle = element('button'); host.toggle.type = 'button'; host.toggle.addEventListener('click', () => {
      optedOut = !optedOut; write(PREF_KEY, optedOut ? '1' : '0'); privacyChanged();
      notify('saved_for_you_toggle', {}, { action: optedOut ? 'off' : 'on' }, host);
    }); controls.append(host.clear, host.toggle); panel.append(controls);
    host.grid = element('div', '', 'sc-saved-grid'); panel.append(host.grid); select(host, 'manual'); render(host);
  }
  function showUndo(item) {
    document.querySelector('.sc-saved-notice')?.remove();
    const notice = element('div', '', 'sc-saved-notice'); notice.setAttribute('role', 'status'); notice.append(element('span', 'Added to Saved for You.'));
    const undo = element('button', 'Undo'); undo.type = 'button'; undo.addEventListener('click', () => { state = dismissItem(state, itemKey(item), now()); save(); notice.remove(); notify('saved_for_you_dismiss', item, { action: 'undo' }); });
    const close = element('button', '×'); close.type = 'button'; close.setAttribute('aria-label', 'Dismiss saved suggestion notice'); close.addEventListener('click', () => notice.remove());
    notice.append(undo, close); document.body.append(notice); setTimeout(() => notice.remove(), 10000);
  }
  const findPages = () => document.querySelectorAll('wishlist-page').forEach(attach);
  new MutationObserver(() => {
    findPages();
    const current = cleanProduct(productForPage());
    if (timedKey && (current ? itemKey(current) : null) !== timedKey) schedule();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] }); findPages();
  const existing = document.querySelector('[data-sc-wishlist-keys]');
  if (existing) { try { manualChanged(JSON.parse(existing.dataset.scWishlistKeys)); } catch {} }
  document.addEventListener('sc:swish-state', event => manualChanged(event.detail));
  document.addEventListener('sc:swish-added', () => hosts.forEach(host => select(host, 'manual')));
  document.addEventListener('sc:saved-existing-manual', event => {
    state = dismissItem(state, event.detail, now()); save(); hosts.forEach(host => select(host, 'manual'));
  });
  document.addEventListener('visitorConsentCollected', privacyChanged);
  document.addEventListener('visibilitychange', () => { privacyChanged(); });
  document.addEventListener('change', event => { if (event.target.name === 'id') schedule(); });
  window.addEventListener('pagehide', () => { pageActive = false; stopTimer(true); document.querySelector('.sc-saved-notice')?.remove(); });
  window.addEventListener('pageshow', event => { if (event.persisted) {
    pageActive = true; stopTimer(true); privacyChanged();
  } });
  window.addEventListener('popstate', () => { stopTimer(true); privacyChanged(); });
  window.addEventListener('storage', event => { if ([DATA_KEY, PREF_KEY].includes(event.key)) { allowed = false; optedOut = read(PREF_KEY) === '1'; privacyChanged(); } });
  privacyChanged();
  if (config.dataset.scSavedConsent !== 'blocked') { let attempts = 0; const poll = setInterval(() => { privacyChanged(); if (consentStatus(config.dataset.scSavedConsent, window.Shopify?.customerPrivacy) !== null || ++attempts >= 15) clearInterval(poll); }, 1000); }
}

if (typeof document !== 'undefined') {
  const config = document.querySelector('[data-sc-saved-config]');
  if (config && !config.dataset.scSavedBound) { config.dataset.scSavedBound = 'true'; startSavedForYou(config); }
}
